import React, { useState, useEffect } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useSelector } from "react-redux";
import { RootState } from "shared/redux/store";
import './index.scss';
import API_SERVICE from '../../shared/services/api-service';
import { INRFormatter } from '../../shared/services/Utility';

interface DataType {
  key: string;
  disbursalId: string;
  caseId: string;
  lenderIdentifier: string;
  lenderName: string;
  borrowerName: string;
  loanProductName: string;
  lenderDisbursementDate: string;
  disbursalDate: string;
  insuranceAmount: number;
  disbursedAmount: number;
  lenderDisbursementAmount: number;
  disbursementDifference: number;
  connectorPayoutComputed: number;
  connectorPayoutRevised: number;
  payoutDifference: number;
  comment?: string;
  salesUserHierarchy: string;
}

interface Props {
  filterValues: any;
  onReconcileDataSequenceComplete?: () => void;
}

const ReconciliationAdjustedDiscrepancies: React.FC<Props> = ({
  filterValues,
  onReconcileDataSequenceComplete,
}) => {
  const [data, setData] = useState<DataType[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const { searchParam } = useSelector((state: RootState) => state.reconciliation);

  useEffect(() => {
    setLoading(true);
    API_SERVICE.getReconcileDisbursedCases({
      pageNo,
      pageSize,
      status: 'RECONCILED_CLOSED_DISCREPANCIES',
      searchParam,
      ...filterValues // Include filter values in the API call
    })
      .then((res: any) => {
        const content = (res?.data?.payload?.content || []).map((row: any) => ({
          ...row,
          key: row.disbursalId || row.key,
          comment: row.adjustDiscrepanciesComment || '',
        }));
        setData(content);
        setTotalCount(res?.data?.payload?.totalElements || 0);
      })
      .finally(() => {
        setLoading(false);
        onReconcileDataSequenceComplete?.();
      });
  }, [pageNo, pageSize, filterValues, searchParam, onReconcileDataSequenceComplete]);

  const handlePaginationChange = (page: number, size: number = pageSize) => {
    setPageNo(page);
    if (size !== pageSize) {
      setPageSize(size);
      setPageNo(1);
    }
  };

  const columns: ColumnsType<DataType> = [
    { title: 'Disbursal ID', dataIndex: 'disbursalId', key: 'disbursalId', fixed: 'left', render: (value: string) => value ?? '' },
    { title: 'Case ID', dataIndex: 'caseId', key: 'caseId', fixed: 'left', render: (value: string) => value ?? '' },
    { title: 'Lender Identifier', dataIndex: 'lenderIdentifier', key: 'lenderIdentifier', render: (value: string) => value ?? '' },
    { title: 'Lender', dataIndex: 'lenderName', key: 'lenderName', render: (value: string) => value ?? '' },
    { title: 'Customer Name', dataIndex: 'borrowerName', key: 'borrowerName', render: (value: string) => value ?? '' },
    { title: 'Product', dataIndex: 'loanProductName', key: 'loanProductName', render: (value: string) => value ?? '' },
    { title: 'Lender Disbursement Date', dataIndex: 'lenderDisbursementDate', key: 'lenderDisbursementDate'},
    { title: 'Disbursement Date', dataIndex: 'disbursalDate', key: 'disbursalDate'},
    { title: 'Insurance Amount', dataIndex: 'insuranceAmount', key: 'insuranceAmount', align: 'right', render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '' },
    { title: 'Disbursement Amount', dataIndex: 'disbursedAmount', key: 'disbursedAmount', align: 'right', render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '' },
    { title: 'Lender Disbursement Amount', dataIndex: 'lenderDisbursementAmount', key: 'lenderDisbursementAmount', align: 'right', render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '' },
    { title: 'Disbursement Difference', dataIndex: 'disbursementDifference', key: 'disbursementDifference', align: 'right', render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '' },
    { title: 'Connector Payout Computed', dataIndex: 'connectorPayoutComputed', key: 'connectorPayoutComputed', align: 'right', render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '' },
    { title: 'Connector Payout Revised', dataIndex: 'connectorPayoutRevised', key: 'connectorPayoutRevised', align: 'right', render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '' },
    { title: 'Payout Difference', dataIndex: 'payoutDifference', key: 'payoutDifference', align: 'right', render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '' },
    { title: 'Comment', dataIndex: 'comment', key: 'comment', render: (text: string) => text ?? '' },
    { title: 'Sales User Hierarchy', dataIndex: 'salesUserHierarchy', key: 'salesUserHierarchy', render: (value: string) => value ?? '' },
  ];

  return (
    <div className="reconciliation-content-box">
      <Table
        className="dynamic-pagination-checkbox table-full-width reconciliation-tight-table reconciliation-table-with-footer"
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="disbursalId"
        scroll={{ x: 'max-content' }}
        size="small"
        pagination={{
          current: pageNo,
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          showQuickJumper: false,
          position: ['bottomRight'],
          onChange: handlePaginationChange,
          onShowSizeChange: handlePaginationChange,
        }}
      />
    </div>
  );
};

export default ReconciliationAdjustedDiscrepancies;
